<template>
  <form>
      <div class="row q-col-gutter-md">
        <div class="col col-xs-12 col-md-6 col-lg-6">
          <q-input
              v-model="AlertObj.title"
              :label="$t('alerts.table_titles.title')"
              lazy-rules
              :error="AlertObj.errors.has('title')"
              :error-message="AlertObj.errors.get('title')"
              :rules="[ val => val && val.length > 0 || 'The Alert is required!']"
          >
            <template slot="hint">
                {{ $t('alerts.title_hint') }}
            </template>
          </q-input>
        </div> <!-- TITLE -->
        <div class="col col-xs-12 col-md-3 col-lg-3">
          <q-select v-model="AlertObj.variant" :options="variants" :label="$t('alerts.table_titles.bg_color')">
            <template v-slot:option="scope">
              <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
              >
                <q-item-section avatar>
                  <q-icon name="square" :class="['bg-'+scope.opt]" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ scope.opt }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>
        <div class="col col-xs-12 col-md-3 col-lg-3">
          <q-select v-model="AlertObj.priority" :options="priorities" emit-value map-options :label="$t('alerts.table_titles.priority')" />
        </div>

        <div class="col-12">
          <q-editor
              v-model="AlertObj.description"
              :dense="$q.screen.lt.md"
              :toolbar="[
                [
                    {
                      label: $q.lang.editor.align,
                      icon: $q.iconSet.editor.align,
                      fixedLabel: true,
                      options: ['left', 'center', 'right', 'justify']
                    }
                ],
                ['bold', 'italic', 'strike', 'underline'],
                ['hr', 'link'],
                ['foreColor', 'backColor'],
                ['unordered', 'ordered'],
                ['fields'],
                ['removeFormat', 'viewsource'],
                ['undo', 'redo']
            ]"
          >
              <template v-slot:foreColor>
                  <q-btn
                      flat
                      no-caps
                      dense
                      style="font-size: 0.7em"
                      icon="fa fa-palette"
                  >
                      <q-tooltip>Fore color</q-tooltip>
                      <q-menu anchor="top left" self="top left">
                          <q-color v-model="foreColor" class="my-picker"></q-color>
                          <q-btn
                              color="primary"
                              style="width: 200px"
                              label="Select"
                              @click="color('foreColor', foreColor)"
                          />
                      </q-menu>
                  </q-btn>
              </template>
              <template v-slot:backColor>
                  <q-btn
                      flat
                      dense
                      no-caps
                      style="font-size: 0.7em"
                      icon="format_paint"
                  >
                      <q-tooltip>Back color</q-tooltip>
                      <q-menu anchor="top left" self="top left">
                          <q-color v-model="backColor" class="my-picker"></q-color>
                          <q-btn
                              color="primary"
                              style="width: 200px"
                              label="Select"
                              @click="color('backColor', backColor)"
                          />
                      </q-menu>
                  </q-btn>
              </template>
              <template v-slot:error>
                  {{ AlertObj.errors.get('body') }}
              </template>
          </q-editor>
        </div>

        <div class="col col-xs-12 col-md-6 col-lg-6">
            <q-input v-model="AlertObj.published_at"
                     :label="$t('alerts.table_titles.published_at')"
                     color="input-color"
            >
                <template v-slot:prepend>
                    <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                        <q-popup-proxy ref="qDateStart" transition-show="scale" transition-hide="scale">
                            <q-date v-model="AlertObj.published_at"
                                    mask="YYYY-MM-DD HH:mm"
                                    today-btn
                                    @input="() => $refs.qDateStart.hide()"
                            />
                        </q-popup-proxy>
                    </q-icon>
                </template>

                <template v-slot:append>
                    <q-icon name="fa fa-clock" class="cursor-pointer">
                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                            <q-time v-model="AlertObj.published_at" mask="YYYY-MM-DD HH:mm" format24h>
                                <div class="row items-center justify-end">
                                    <q-btn v-close-popup label="Set" color="primary" flat />
                                </div>
                            </q-time>
                        </q-popup-proxy>
                    </q-icon>
                </template>
            </q-input>
        </div>

        <div class="col col-xs-12 col-md-6 col-lg-6">
              <q-input v-model="AlertObj.expired_at"
                       :label="$t('alerts.table_titles.expired_at')"
                       color="input-color"
              >
                  <template v-slot:prepend>
                      <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                          <q-popup-proxy ref="qDateStart1" transition-show="scale" transition-hide="scale">
                              <q-date v-model="AlertObj.expired_at"
                                      mask="YYYY-MM-DD HH:mm"
                                      today-btn
                                      @input="() => $refs.qDateStart1.hide()"
                              />
                          </q-popup-proxy>
                      </q-icon>
                  </template>

                  <template v-slot:append>
                      <q-icon name="fa fa-clock" class="cursor-pointer">
                          <q-popup-proxy transition-show="scale" transition-hide="scale">
                              <q-time v-model="AlertObj.expired_at" mask="YYYY-MM-DD HH:mm" format24h>
                                  <div class="row items-center justify-end">
                                      <q-btn v-close-popup label="Set" color="primary" flat />
                                  </div>
                              </q-time>
                          </q-popup-proxy>
                      </q-icon>
                  </template>
              </q-input>
          </div>

        <div class="col col-xs-12 col-sm-6 col-md-4">
          <p class="text-caption q-pb-xs" style="margin:0">{{$t('alerts.table_titles.display_on_login')}}</p>
          <q-toggle
              v-model="AlertObj.display_on_login"
              checked-icon="fa fa-check"
              color="green"
              :label="AlertObj.display_on_login ? $t('alerts.yes') : $t('alerts.no')"
              unchecked-icon="fa fa-times"
          />
        </div> <!-- DISPLAY ON LOGIN -->
        <div class="col col-xs-12 col-sm-6 col-md-4">
              <p class="text-caption q-pb-xs" style="margin:0">{{$t('alerts.table_titles.display_on_landing')}}</p>
              <q-toggle
                  v-model="AlertObj.display_on_landing"
                  checked-icon="fa fa-check"
                  color="green"
                  :label="AlertObj.display_on_landing ? $t('alerts.yes') : $t('alerts.no')"
                  unchecked-icon="fa fa-times"
              />
          </div> <!-- DISPLAY ON LANDING -->
        <div class="col col-xs-12 col-sm-6 col-md-4">
              <p class="text-caption q-pb-xs" style="margin:0">{{$t('alerts.table_titles.state')}}</p>
              <q-toggle
                  v-model="AlertObj.is_active"
                  checked-icon="fa fa-check"
                  color="green"
                  :label="AlertObj.is_active ? $t('alerts.active') : $t('alerts.inactive')"
                  unchecked-icon="fa fa-times"
              />
          </div> <!-- Active -->

      </div>
    </form>
</template>

<script>

export default {
  name: 'Form',
  model: {
    prop: 'AlertObj'
  },
  props: {
    AlertObj: {
      type: Object,
      required: true
    }
  },
  computed: {},
  data() {
    return {
      variants: ['primary', 'secondary', 'accent', 'dark', 'positive', 'negative', 'info', 'warning'],
      priorities: [
        {label: 'Low', value: 0},
        {label: 'Medium', value: 1},
        {label: 'High', value: 2}
      ],
      foreColor: '',
      backColor: '',
    }
  },
  methods: {
    submit() {
      this.$emit('submit')
    },
    color (cmd, name) {
        const edit = this.$refs.editor
        edit.caret.restore()
        edit.runCmd(cmd, name, true)
        edit.focus()
    },
  },
  updated() {
    this.$emit('change', this.AlertObj)
  },
  components: {},
}
</script>

<style scoped>

</style>
